import React, { useEffect, useState } from 'react';
import { Form, Card, Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
import { FaArrowLeft } from 'react-icons/fa';
import Select from 'react-select';
import debounce from 'lodash/debounce';

function PumpProductionProcessCreate() {
  const navigate = useNavigate();
  const [productionOrderSearch, setProductionOrderSearch] = useState(encodeURIComponent(" "));
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null); // New state for details

  const [stageStatus, setStageStatus] = useState({
    roughmachine: '',
    fabrication: '',
    finalmachining: ''
  });

  const [stageDates, setStageDates] = useState({
    roughmachine: '',
    fabrication: '',
    finalmachining: ''
  });

  const [inspectionData, setInspectionData] = useState({
    acceptorreject: '',
    rejectionreason: '',
    inspectedbyone: '',
    inspectiondateone: '',
    inspectedbytwo: '',
    inspectiondatetwo: '',
    inspectedbythree: '',
    inspectiondatethree: '',
    remarks: ''
  });

  const handleSearchProductionOrder = debounce((inputValue) => {
    setProductionOrderSearch(encodeURIComponent(inputValue));
  }, 500);

  const fetchProductionOrders = async () => {
    try {
      const res = await axios.get(`${BASE_URL}productionorder/bytype/page/0/5/asc/id/pump/${productionOrderSearch}`, {
        headers: authHeader(),
      });

      const list = res.data.Index || res.data.data;

      setProductionOrders(list.map(item => ({
        value: item.id,
        label: `${item.productionordernumber}`,
        jobnumber: item.jobnumber,
        partno: item.partno
      })));
    } catch (error) {
      console.error("Error fetching production orders", error);
    }
  };

  const handleProductionOrderSelect = async (selected) => {
    setSelectedProductionOrder(selected);
    
    if (selected) {
      try {
        // Fetch complete order details
        const response = await axios.get(`${BASE_URL}productionorder/${selected.value}`, {
          headers: authHeader(),
        });
        
        setSelectedOrderDetails({
          jobnumber: response.data.jobnumber,
          partno: response.data.partno,
          productionordernumber: response.data.productionordernumber
        });
      } catch (error) {
        console.error("Error fetching order details", error);
      }
    } else {
      setSelectedOrderDetails(null);
    }
  };

  useEffect(() => {
    fetchProductionOrders();
  }, [productionOrderSearch]);

  const handleChange = (e, stage) => {
    setStageStatus(prev => ({
      ...prev,
      [stage]: e.target.value
    }));
  };

  const handleDateChange = (stage, value) => {
    setStageDates(prev => ({
      ...prev,
      [stage]: value
    }));
  };

  const handleInspectionChange = (e) => {
    const { name, value } = e.target;
    setInspectionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isRoughMachiningComplete = stageStatus.roughmachine === 'completed' &&
    stageDates.roughmachine;

  const isFabricationComplete = stageStatus.fabrication === 'completed' &&
    stageDates.fabrication;

  const isFinalMachiningComplete = stageStatus.finalmachining === 'completed' &&
    stageDates.finalmachining;

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(stageStatus)) {
      if (value === 'completed' && !stageDates[key]) {
        Swal.fire('Error', `Please enter date for ${key}`, 'error');
        return;
      }
    }

    const pumpData = {
      ...stageStatus,
      ...inspectionData
    };

    Object.entries(stageDates).forEach(([key, date]) => {
      if (date) {
        pumpData[`${key}date`] = date;
      }
    });

    const payload = {
      type: "Pump",
      productionordernumber: selectedProductionOrder?.label,
      jobnumber: selectedOrderDetails?.jobnumber,
      partno: selectedOrderDetails?.partno,
      pump: [pumpData]
    };

    try {
      // 1. POST pump process data
      await axios.post(`${BASE_URL}productionprocess/withPump`, payload, {
        headers: authHeader(),
      });

      // 2. GET popumpitems for the selected production order
      const response = await axios.get(`${BASE_URL}productionorder/${selectedProductionOrder.value}`, {
        headers: authHeader(),
      });

      const popumpitems = response.data.popumpitems || [];

      const status = inspectionData.acceptorreject === 'accepted' ? 'Closed' : 'Open';

      await Promise.all(
        popumpitems.map(item => {
          const bomUpdate = axios.put(`${BASE_URL}bom/update/status/${item.id}/Closed`, {}, {
            headers: authHeader(),
          });

          const productionProcessUpdate = axios.put(
            `${BASE_URL}productionorder/update/status/${item.id}/${status}`,
            {},
            { headers: authHeader() }
          );

          return Promise.all([bomUpdate, productionProcessUpdate]);
        })
      );

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Data submitted',
      })
        .then(() => navigate("/pump-production-process-list/1"));
    } catch (error) {
      console.error("Error submitting or updating", error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to submit or update data.',
      });
    }
  };

  const showInspectionFields = isFinalMachiningComplete;

  return (
    <Card>
      <Card.Header>
        <h6 className="fw-bold text-primary">Pump Production Process Entry</h6>
        <div className="d-flex justify-content-end gap-2">
          <Link to={`/pump-production-process-list/1`}>
            <Button variant="outline-secondary" size="sm">
              <FaArrowLeft className="me-2" /> BACK TO LIST
            </Button>
          </Link>
        </div>
      </Card.Header>

      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Select a Production Order Number</Form.Label>
                <Select
                  options={productionOrders}
                  onChange={handleProductionOrderSelect}
                  onInputChange={handleSearchProductionOrder}
                  value={selectedProductionOrder}
                  placeholder="Search or select production order"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Display Job Number and Part No in separate fields */}
          {selectedOrderDetails && (
            <Row className="mt-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Job Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedOrderDetails.jobnumber || ''}
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Part No</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedOrderDetails.partno || ''}
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <hr />

          {/* Rough Machining */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Rough Machining</Form.Label>
                <Form.Select
                  value={stageStatus.roughmachine}
                  onChange={(e) => handleChange(e, 'roughmachine')}
                >
                  <option value="">Select Rough Machining Status</option>
                  <option value="pending">❌ Pending</option>
                  <option value="inProgress">🟡 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </Form.Select>

                {stageStatus.roughmachine === 'completed' && (
                  <>
                    <Form.Label className="mt-2">Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={stageDates.roughmachine}
                      onChange={(e) => handleDateChange('roughmachine', e.target.value)}
                    />
                  </>
                )}
              </Form.Group>
            </Col>

            {/* Fabrication */}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Fabrication</Form.Label>
                <Form.Select
                  value={stageStatus.fabrication}
                  onChange={(e) => handleChange(e, 'fabrication')}
                  disabled={!isRoughMachiningComplete}
                >
                  <option value="">Select Fabrication Status</option>
                  <option value="pending">❌ Pending</option>
                  <option value="inProgress">🟡 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </Form.Select>

                {stageStatus.fabrication === 'completed' && isRoughMachiningComplete && (
                  <>
                    <Form.Label className="mt-2">Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={stageDates.fabrication}
                      onChange={(e) => handleDateChange('fabrication', e.target.value)}
                    />
                  </>
                )}
              </Form.Group>
            </Col>

            {/* Final Machining */}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Final Machining</Form.Label>
                <Form.Select
                  value={stageStatus.finalmachining}
                  onChange={(e) => handleChange(e, 'finalmachining')}
                  disabled={!isFabricationComplete}
                >
                  <option value="">Select Final Machining Status</option>
                  <option value="pending">❌ Pending</option>
                  <option value="inProgress">🟡 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </Form.Select>

                {stageStatus.finalmachining === 'completed' && isFabricationComplete && (
                  <>
                    <Form.Label className="mt-2">Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={stageDates.finalmachining}
                      onChange={(e) => handleDateChange('finalmachining', e.target.value)}
                    />
                  </>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* ... rest of your code remains same ... */}
          <hr />

          {/* Inspection Section */}
          {showInspectionFields && (
            <>
              <Row className="mt-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Accepted / Rejected</Form.Label>
                    <Form.Select
                      name="acceptorreject"
                      value={inspectionData.acceptorreject}
                      onChange={handleInspectionChange}
                    >
                      <option value="">Select</option>
                      <option value="accepted">Accepted ✅</option>
                      <option value="rejected">Rejected ❌</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Rejection Reason</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="rejectionreason"
                      rows={2}
                      value={inspectionData.rejectionreason}
                      onChange={handleInspectionChange}
                      disabled={inspectionData.acceptorreject !== 'rejected'}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Inspected By 1</Form.Label>
                    <Form.Control
                      name="inspectedbyone"
                      value={inspectionData.inspectedbyone}
                      onChange={handleInspectionChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Inspection Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="inspectiondateone"
                      value={inspectionData.inspectiondateone}
                      onChange={handleInspectionChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <Row>
                <Col md={6}>
                  <Form.Group controlId="inspectedbytwo">
                    <Form.Label>Inspected By 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="inspectedbytwo"
                      value={inspectionData.inspectedbytwo}
                      onChange={handleInspectionChange}
                      placeholder="Inspector's name"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="inspectiondatetwo">
                    <Form.Label>Inspection Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="inspectiondatetwo"
                      value={inspectionData.inspectiondatetwo}
                      onChange={handleInspectionChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={6}>
                  <Form.Group controlId="inspectedbythree">
                    <Form.Label>Inspected By 3</Form.Label>
                    <Form.Control
                      type="text"
                      name="inspectedbythree"
                      value={inspectionData.inspectedbythree}
                      onChange={handleInspectionChange}
                      placeholder="Inspector's name"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="inspectiondatethree">
                    <Form.Label>Inspection Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="inspectiondatethree"
                      value={inspectionData.inspectiondatethree}
                      onChange={handleInspectionChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={12}>
                  <Form.Group controlId="remarks">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="remarks"
                      value={inspectionData.remarks}
                      onChange={handleInspectionChange}
                      placeholder="Additional comments"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          <Button type="submit" className="mt-4" variant="primary">
            Submit
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default PumpProductionProcessCreate;